import React, { type ReactElement, type ReactNode, useState, useEffect } from 'react';
import VpChevronDownIcon from '@vtmn-play/icons/react/VpChevronDownIcon';
import { v7 } from 'uuid';

import useClickOutside from '../../../utils/hooks/useClickOutside';
import { AppButton, type AppButtonProps } from '../Buttons/AppButton';

import style from './button-dropdown.module.css';

interface ButtonDropdownProps {
  readonly label: string;
  readonly children: ReactNode;
  readonly className?: string;
  readonly startSlot?: ReactElement;
  readonly variant?: AppButtonProps['variant'];
  readonly id?: string;
  readonly onClose?: () => void;
  readonly onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  readonly contentRole?: React.AriaRole;
  readonly 'aria-label'?: string;
  readonly 'data-testid'?: string;
}

export const ButtonDropdown = ({
  label,
  children,
  id,
  onClose,
  contentRole,
  'aria-label': ariaLabel,
  onKeyDown,
  className,
  startSlot,
  variant,
  'data-testid': dataTestId,
}: ButtonDropdownProps) => {
  const [isVisible, setIsVisible] = useState(false);

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  const dropdownRef = useClickOutside<HTMLDivElement>(handleClose);
  const tagId = id ?? v7();
  const contentId = `${tagId}-content`;
  const toggleVisibility = () => {
    if (isVisible) {
      handleClose();
    } else {
      setIsVisible(true);
    }
  };

  useEffect(() => {
    if (!isVisible) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
        document.getElementById(tagId)?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVisible]);

  return (
    <div ref={dropdownRef} className={style['dropdown-container']}>
      <AppButton
        data-testid={dataTestId}
        id={tagId}
        className={className}
        startSlot={startSlot}
        variant={variant}
        onClick={toggleVisibility}
        onKeyDown={onKeyDown}
        endSlot={<VpChevronDownIcon />}
        aria-expanded={isVisible}
        aria-haspopup="true"
        aria-controls={isVisible ? contentId : undefined}
        aria-label={ariaLabel}
      >
        {label}
      </AppButton>
      {isVisible ? (
        <div id={contentId} className={style['dropdown-content']} role={contentRole}>
          {children}
        </div>
      ) : null}
    </div>
  );
};
